import { useCallback, useEffect, useState } from 'react';

// Lembra a rolagem (vertical e horizontal) de um elemento por chave, em
// sessionStorage: sair da página e voltar reencontra a mesma posição. O
// conteúdo costuma chegar depois (lista/board carregando) — a posição é
// reaplicada a cada mudança de tamanho/conteúdo até caber, e desiste se a
// pessoa rolar por conta própria ou depois de alguns segundos.

interface ScrollPos { top: number; left: number }

const RESTORE_TIMEOUT_MS = 8000;

function read(storageKey: string): ScrollPos | null {
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as ScrollPos) : null;
  } catch {
    return null;
  }
}

function write(storageKey: string, pos: ScrollPos): void {
  try { sessionStorage.setItem(storageKey, JSON.stringify(pos)); } catch { /* ignora */ }
}

/**
 * Devolve um ref de callback para o elemento que rola — liga quando o elemento
 * aparece (ex.: o board só monta depois do skeleton).
 * @param key  null desliga.
 * @param options.resetWhenEmpty  sem posição guardada, volta ao topo/início —
 *   para um contêiner que sobrevive à troca de página (a área principal).
 */
export function useScrollMemory(key: string | null, options?: { resetWhenEmpty?: boolean }) {
  const resetWhenEmpty = options?.resetWhenEmpty ?? false;
  const [el, setEl] = useState<HTMLElement | null>(null);
  const ref = useCallback((node: HTMLElement | null) => setEl(node), []);

  useEffect(() => {
    if (!el || !key) return;
    const storageKey = `sophia:scroll:${key}`;
    const saved = read(storageKey);
    let restoring = !!saved && (saved.top > 0 || saved.left > 0);

    const stop = () => {
      if (!restoring) return;
      restoring = false;
      mutations.disconnect();
      resizes.disconnect();
      clearTimeout(timer);
    };
    const tryRestore = () => {
      if (!restoring || !saved) return;
      el.scrollTop = saved.top;
      el.scrollLeft = saved.left;
      if (Math.abs(el.scrollTop - saved.top) < 2 && Math.abs(el.scrollLeft - saved.left) < 2) stop();
    };

    const mutations = new MutationObserver(tryRestore);
    const resizes = new ResizeObserver(tryRestore);
    const timer = setTimeout(stop, RESTORE_TIMEOUT_MS);
    if (restoring) {
      mutations.observe(el, { childList: true, subtree: true });
      resizes.observe(el);
      tryRestore();
    } else if (resetWhenEmpty) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }

    // A pessoa rolou por conta própria: a posição dela vale mais.
    const userScroll = () => stop();
    el.addEventListener('wheel', userScroll, { passive: true });
    el.addEventListener('touchstart', userScroll, { passive: true });
    el.addEventListener('keydown', userScroll);
    el.addEventListener('pointerdown', userScroll);

    // setTimeout e não requestAnimationFrame: rAF não roda com a aba oculta.
    let pending: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      if (restoring) return;
      clearTimeout(pending);
      pending = setTimeout(() => write(storageKey, { top: el.scrollTop, left: el.scrollLeft }), 80);
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      stop();
      clearTimeout(pending);
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', userScroll);
      el.removeEventListener('touchstart', userScroll);
      el.removeEventListener('keydown', userScroll);
      el.removeEventListener('pointerdown', userScroll);
    };
  }, [key, el, resetWhenEmpty]);

  return ref;
}
